import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  alpha,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Select,
  Snackbar,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";

import PhoneInTalkIcon from "@mui/icons-material/PhoneInTalk";
import LocalPhoneIcon from "@mui/icons-material/LocalPhone";
import CallReceivedIcon from "@mui/icons-material/CallReceived";
import PersonSearchIcon from "@mui/icons-material/PersonSearch";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import StorefrontIcon from "@mui/icons-material/Storefront";
import TableRestaurantIcon from "@mui/icons-material/TableRestaurant";
import PaymentsIcon from "@mui/icons-material/Payments";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import InventoryIcon from "@mui/icons-material/Inventory";
import SearchIcon from "@mui/icons-material/Search";
import RefreshIcon from "@mui/icons-material/Refresh";
import SaveIcon from "@mui/icons-material/Save";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import HistoryIcon from "@mui/icons-material/History";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import DoneAllIcon from "@mui/icons-material/DoneAll";

const API_BASE =
  process.env.REACT_APP_API_BASE ||
  "https://panel.haymestasarim.com/api";

const BRAND = {
  blue: "#0B4D8B",
  blue2: "#1565C0",
  orange: "#F58220",
  orange2: "#FFB15C",
  dark: "#0F172A",
  ink: "#1E293B",
  muted: "#64748B",
  bg: "#F8FAFC",
  softBlue: "#EAF4FF",
  softOrange: "#FFF3E6",
  success: "#10B981",
  danger: "#EF4444",
  warning: "#F59E0B",
  info: "#06B6D4",
  border: "rgba(15,23,42,0.10)",
};

function TelefonGetToken() {
  return (
    localStorage.getItem("token") ||
    localStorage.getItem("authToken") ||
    localStorage.getItem("accessToken") ||
    ""
  );
}

function TelefonHeaders(extra = {}) {
  const token = TelefonGetToken();

  return {
    "Content-Type": "application/json",
    Accept: "application/json",
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...extra,
  };
}

async function TelefonApi(path, options = {}) {
  const res = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers: TelefonHeaders(options.headers || {}),
  });

  const text = await res.text();
  let data = {};

  try {
    data = text ? JSON.parse(text) : {};
  } catch {
    data = {
      ok: false,
      message: text || "Geçersiz sunucu cevabı",
    };
  }

  if (!res.ok || data.ok === false || data.success === false) {
    const err = new Error(
      data.message ||
        data.error ||
        data.detail ||
        `İşlem başarısız: HTTP ${res.status}`
    );
    err.status = res.status;
    err.data = data;
    throw err;
  }

  return data;
}

function TelefonGetLang() {
  const raw =
    localStorage.getItem("panel_dili") ||
    localStorage.getItem("app_lang") ||
    localStorage.getItem("lang") ||
    "tr";

  const lang = String(raw).slice(0, 2).toLowerCase();
  return ["tr", "en", "de"].includes(lang) ? lang : "tr";
}

function TelefonLocale(lang) {
  if (lang === "en") return "en-US";
  if (lang === "de") return "de-DE";
  return "tr-TR";
}

function TelefonMoney(value, currency = "TRY", lang = "tr") {
  const n = Number(value || 0);

  return `${n.toLocaleString(TelefonLocale(lang), {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })} ${String(currency || "TRY").toUpperCase()}`;
}

function TelefonDigits(value) {
  return String(value || "").replace(/\D/g, "");
}

function TelefonSafeNumber(value) {
  const n = Number(value || 0);
  return Number.isFinite(n) ? n : 0;
}

function TelefonDate(value, lang = "tr") {
  if (!value) return "-";

  try {
    return new Date(value).toLocaleString(TelefonLocale(lang), {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return String(value);
  }
}

const TELEFON_TEXT = {
  tr: {
    title: "Telefon Siparişi",
    subtitle:
      "Arayan müşteriyi hızlıca bulun, Cari’ye kaydedin, siparişi stok ve kasa ile entegre yönetin.",
    tabOrder: "Sipariş Gir",
    tabCari: "Cari Listesi",
    tabOrders: "Telefon Siparişleri",
    tabCalls: "Gelen Aramalar",
    refresh: "Yenile",
    incomingCall: "Gelen Arama",
    incomingCallText:
      "Santral bağlandığında arayan numara bu alana otomatik düşebilir. Şimdilik numarayı yazarak müşteriyi bulabilirsiniz.",
    phone: "Telefon",
    phonePlaceholder: "05xx xxx xx xx",
    customerInfo: "Müşteri Bilgileri",
    customerName: "Ad Soyad",
    address: "Adres",
    apartment: "Apartman / Daire",
    customerNote: "Müşteri Notu",
    orderNote: "Sipariş Notu",
    deliveryType: "Teslimat Tipi",
    delivery: "Paket Servis",
    pickup: "Gel-Al",
    table: "Masa",
    tableNo: "Masa No",
    paymentStatus: "Ödeme Durumu",
    paid: "Ödendi",
    unpaid: "Ödenmedi",
    paymentMethod: "Ödeme Türü",
    cash: "Nakit",
    card: "Kart",
    bank: "Banka",
    online: "Online",
    staff: "Personel",
    findCustomer: "Müşteri Ara",
    foundCustomer: "Kayıtlı müşteri bulundu",
    newCustomer: "Yeni müşteri olarak Cari’ye kaydedilecek",
    customerResults: "Müşteri Sonuçları",
    select: "Seç",
    productSearch: "Ürün Ara",
    productSearchPlaceholder: "Stoktaki ürün adı, barkod veya kod ara",
    stockProducts: "Stok Ürünleri",
    criticalStock: "Kritik stok",
    outOfStock: "Stok yok",
    add: "Ekle",
    manualItem: "Manuel Ürün",
    manualItemText:
      "Stokta olmayan ürün/hizmet için manuel kalem ekleyebilirsiniz.",
    itemName: "Ürün / Hizmet Adı",
    quantity: "Adet",
    unitPrice: "Birim Fiyat",
    currency: "Para Birimi",
    addManualItem: "Manuel Kalem Ekle",
    basket: "Sepet",
    emptyBasket: "Sepet boş.",
    remove: "Sil",
    total: "Toplam",
    completeOrder: "Telefon Siparişini Kaydet",
    saving: "Kaydediliyor",
    saved: "Telefon siparişi kaydedildi.",
    savedWithCari: "Telefon siparişi kaydedildi ve müşteri Cari’ye işlendi.",
    cariSaved: "Müşteri Cari listesine işlendi.",
    cariAlready: "Müşteri Cari listesinde kayıtlı.",
    requiredPhone: "Telefon numarası zorunlu.",
    requiredName: "Müşteri adı zorunlu.",
    requiredBasket: "Sepet boş olamaz.",
    serverError: "Sunucuya bağlanılamadı.",
    insufficientStock:
      "Stok kaydı yapılamadı. Ürün stoğu yetersiz olabilir.",
    sourcePhone: "Telefon Siparişi",
    integrationReady: "Cari + Stok + Kasa uyumlu",
    integrationText:
      "Telefon siparişinde müşteri Cari’ye gider, stoktan düşer ve ödeme alındıysa kasa kaydı oluşur.",
    reset: "Temizle",
    allowNegativeStock: "Negatif stok satışına izin ver",
    stockCount: "Stok",
    piece: "adet",
    selectedCustomer: "Seçili müşteri",
    cariList: "Cari Listesi",
    cariSearch: "Cari adı, telefon veya adres ara",
    noCari: "Henüz cari kaydı yok.",
    noOrder: "Henüz telefon siparişi yok.",
    noCall: "Henüz gelen arama yok.",
    orderHistory: "Telefon Siparişleri",
    callHistory: "Gelen Aramalar",
    status: "Durum",
    new: "Yeni",
    preparing: "Hazırlanıyor",
    ready: "Hazır",
    delivered: "Teslim Edildi",
    completed: "Tamamlandı",
    cancelled: "İptal",
    markPreparing: "Hazırlanıyor",
    markCompleted: "Tamamla",
    markRead: "Okundu Yap",
    sourceCari: "Cari",
    sourceTelefon: "Telefon",
    sourceBeauty: "Beauty",
    totalCari: "Toplam Cari",
    totalOrders: "Telefon Siparişi",
    paidOrders: "Ödenen",
    unpaidOrders: "Ödenmeyen",
    active: "Aktif",
    passive: "Pasif",
    addressShort: "Adres",
    lastOrders: "Son Siparişler",
    addToCariInfo:
      "Sipariş kaydedilirken müşteri telefon numarasıyla Cari listesinde aranır. Kayıt yoksa otomatik yeni Cari açılır.",
  },
  en: {
    title: "Phone Order",
    subtitle:
      "Find the caller quickly, save them to customer accounts and manage the order with stock and cash integration.",
    tabOrder: "Create Order",
    tabCari: "Customer List",
    tabOrders: "Phone Orders",
    tabCalls: "Incoming Calls",
    refresh: "Refresh",
    incomingCall: "Incoming Call",
    incomingCallText:
      "When PBX is connected, caller number can appear here automatically. For now, staff can type the number manually.",
    phone: "Phone",
    phonePlaceholder: "+90 5xx xxx xx xx",
    customerInfo: "Customer Information",
    customerName: "Full Name",
    address: "Address",
    apartment: "Apartment / Flat",
    customerNote: "Customer Note",
    orderNote: "Order Note",
    deliveryType: "Delivery Type",
    delivery: "Delivery",
    pickup: "Pickup",
    table: "Table",
    tableNo: "Table No",
    paymentStatus: "Payment Status",
    paid: "Paid",
    unpaid: "Unpaid",
    paymentMethod: "Payment Method",
    cash: "Cash",
    card: "Card",
    bank: "Bank",
    online: "Online",
    staff: "Staff",
    findCustomer: "Find Customer",
    foundCustomer: "Registered customer found",
    newCustomer: "Will be saved to customer accounts",
    customerResults: "Customer Results",
    select: "Select",
    productSearch: "Search Product",
    productSearchPlaceholder: "Search stock product, barcode or code",
    stockProducts: "Stock Products",
    criticalStock: "Critical stock",
    outOfStock: "Out of stock",
    add: "Add",
    manualItem: "Manual Item",
    manualItemText:
      "You can add a manual product/service that is not in stock.",
    itemName: "Product / Service Name",
    quantity: "Qty",
    unitPrice: "Unit Price",
    currency: "Currency",
    addManualItem: "Add Manual Item",
    basket: "Basket",
    emptyBasket: "Basket is empty.",
    remove: "Remove",
    total: "Total",
    completeOrder: "Save Phone Order",
    saving: "Saving",
    saved: "Phone order saved.",
    savedWithCari:
      "Phone order saved and customer was synced to customer accounts.",
    cariSaved: "Customer was saved to customer accounts.",
    cariAlready: "Customer already exists in customer accounts.",
    requiredPhone: "Phone number is required.",
    requiredName: "Customer name is required.",
    requiredBasket: "Basket cannot be empty.",
    serverError: "Could not connect to server.",
    insufficientStock:
      "Stock sale could not be completed. Product stock may be insufficient.",
    sourcePhone: "Phone Order",
    integrationReady: "Customer + Stock + Cash ready",
    integrationText:
      "Phone order syncs customer account, decreases stock and creates cash record when paid.",
    reset: "Clear",
    allowNegativeStock: "Allow negative stock sale",
    stockCount: "Stock",
    piece: "pcs",
    selectedCustomer: "Selected customer",
    cariList: "Customer List",
    cariSearch: "Search name, phone or address",
    noCari: "No customer records yet.",
    noOrder: "No phone orders yet.",
    noCall: "No incoming calls yet.",
    orderHistory: "Phone Orders",
    callHistory: "Incoming Calls",
    status: "Status",
    new: "New",
    preparing: "Preparing",
    ready: "Ready",
    delivered: "Delivered",
    completed: "Completed",
    cancelled: "Cancelled",
    markPreparing: "Preparing",
    markCompleted: "Complete",
    markRead: "Mark Read",
    sourceCari: "Customer",
    sourceTelefon: "Phone",
    sourceBeauty: "Beauty",
    totalCari: "Total Customers",
    totalOrders: "Phone Orders",
    paidOrders: "Paid",
    unpaidOrders: "Unpaid",
    active: "Active",
    passive: "Passive",
    addressShort: "Address",
    lastOrders: "Recent Orders",
    addToCariInfo:
      "When saving the order, customer is searched by phone. If not found, a new customer account is created automatically.",
  },
  de: {
    title: "Telefonbestellung",
    subtitle:
      "Anrufer schnell finden, in die Kundenliste speichern und Bestellung mit Lager und Kasse verwalten.",
    tabOrder: "Bestellung",
    tabCari: "Kundenliste",
    tabOrders: "Telefonbestellungen",
    tabCalls: "Eingehende Anrufe",
    refresh: "Aktualisieren",
    incomingCall: "Eingehender Anruf",
    incomingCallText:
      "Wenn PBX verbunden ist, kann die Rufnummer automatisch erscheinen. Aktuell kann das Personal die Nummer manuell eingeben.",
    phone: "Telefon",
    phonePlaceholder: "+49 / +90 ...",
    customerInfo: "Kundendaten",
    customerName: "Name Nachname",
    address: "Adresse",
    apartment: "Haus / Wohnung",
    customerNote: "Kundennotiz",
    orderNote: "Bestellnotiz",
    deliveryType: "Lieferart",
    delivery: "Lieferung",
    pickup: "Abholung",
    table: "Tisch",
    tableNo: "Tisch Nr.",
    paymentStatus: "Zahlungsstatus",
    paid: "Bezahlt",
    unpaid: "Unbezahlt",
    paymentMethod: "Zahlungsart",
    cash: "Bar",
    card: "Karte",
    bank: "Bank",
    online: "Online",
    staff: "Personal",
    findCustomer: "Kunde suchen",
    foundCustomer: "Registrierter Kunde gefunden",
    newCustomer: "Wird in die Kundenliste gespeichert",
    customerResults: "Kundenergebnisse",
    select: "Auswählen",
    productSearch: "Produkt suchen",
    productSearchPlaceholder: "Lagerprodukt, Barcode oder Code suchen",
    stockProducts: "Lagerprodukte",
    criticalStock: "Kritischer Bestand",
    outOfStock: "Kein Bestand",
    add: "Hinzufügen",
    manualItem: "Manueller Artikel",
    manualItemText:
      "Sie können ein Produkt/eine Leistung hinzufügen, die nicht im Lager ist.",
    itemName: "Produkt / Leistung",
    quantity: "Menge",
    unitPrice: "Einzelpreis",
    currency: "Währung",
    addManualItem: "Manuellen Artikel hinzufügen",
    basket: "Warenkorb",
    emptyBasket: "Warenkorb ist leer.",
    remove: "Entfernen",
    total: "Gesamt",
    completeOrder: "Telefonbestellung speichern",
    saving: "Speichern",
    saved: "Telefonbestellung gespeichert.",
    savedWithCari:
      "Telefonbestellung gespeichert und Kunde in die Kundenliste übertragen.",
    cariSaved: "Kunde wurde in die Kundenliste gespeichert.",
    cariAlready: "Kunde existiert bereits in der Kundenliste.",
    requiredPhone: "Telefonnummer ist erforderlich.",
    requiredName: "Kundenname ist erforderlich.",
    requiredBasket: "Warenkorb darf nicht leer sein.",
    serverError: "Serververbindung fehlgeschlagen.",
    insufficientStock:
      "Lagerverkauf konnte nicht abgeschlossen werden. Bestand kann unzureichend sein.",
    sourcePhone: "Telefonbestellung",
    integrationReady: "Kunde + Lager + Kasse bereit",
    integrationText:
      "Telefonbestellung erstellt Kundendaten, reduziert Lager und erstellt Kassenbuchung bei bezahlten Bestellungen.",
    reset: "Leeren",
    allowNegativeStock: "Negativen Lagerverkauf erlauben",
    stockCount: "Bestand",
    piece: "Stk.",
    selectedCustomer: "Ausgewählter Kunde",
    cariList: "Kundenliste",
    cariSearch: "Name, Telefon oder Adresse suchen",
    noCari: "Noch keine Kunden vorhanden.",
    noOrder: "Noch keine Telefonbestellungen.",
    noCall: "Noch keine eingehenden Anrufe.",
    orderHistory: "Telefonbestellungen",
    callHistory: "Eingehende Anrufe",
    status: "Status",
    new: "Neu",
    preparing: "In Vorbereitung",
    ready: "Bereit",
    delivered: "Geliefert",
    completed: "Abgeschlossen",
    cancelled: "Storniert",
    markPreparing: "Vorbereiten",
    markCompleted: "Abschließen",
    markRead: "Gelesen",
    sourceCari: "Kunde",
    sourceTelefon: "Telefon",
    sourceBeauty: "Beauty",
    totalCari: "Kunden",
    totalOrders: "Telefonbestellungen",
    paidOrders: "Bezahlt",
    unpaidOrders: "Unbezahlt",
    active: "Aktiv",
    passive: "Passiv",
    addressShort: "Adresse",
    lastOrders: "Letzte Bestellungen",
    addToCariInfo:
      "Beim Speichern wird der Kunde per Telefonnummer gesucht. Wenn nicht vorhanden, wird automatisch ein neuer Kunde erstellt.",
  },
};

const TelefonEmptyCustomer = {
  customer_id: "",
  customer_source: "",
  full_name: "",
  phone: "",
  address: "",
  apartment: "",
  customer_note: "",
};

const TelefonEmptyOrder = {
  delivery_type: "delivery",
  table_no: "",
  payment_status: "unpaid",
  payment_method: "cash",
  staff_name: "",
  order_note: "",
  allow_negative_stock: false,
};

const TelefonEmptyManualItem = {
  name: "",
  quantity: "1",
  unit_price: "",
  currency: "TRY",
};

function TelefonNormalizeCustomer(raw = {}, source = "customer") {
  const fullName =
    raw.full_name ||
    raw.name ||
    raw.ad_soyad ||
    raw.customer_name ||
    raw.musteri_adi ||
    "";

  const phone =
    raw.phone ||
    raw.telefon ||
    raw.customer_phone ||
    raw.gsm ||
    "";

  const address =
    raw.address ||
    raw.adres ||
    raw.customer_address ||
    raw.musteri_adres ||
    "";

  return {
    id: raw.id || raw.customer_id || "",
    customer_id: raw.customer_id_for_cash || raw.id || raw.customer_id || "",
    customer_source: raw.customer_source || source,
    full_name: String(fullName || "").trim(),
    phone: String(phone || "").trim(),
    address: String(address || "").trim(),
    apartment: raw.apartment || raw.daire || raw.flat || "",
    customer_note: raw.note || raw.notes || raw.customer_note || "",
    email: raw.email || "",
    city: raw.city || raw.il || "",
    aktif: raw.aktif !== false && raw.active !== false,
    raw,
  };
}

function TelefonNormalizeStock(item = {}) {
  return {
    ...item,
    id: String(item.id || item.backend_id || item.frontend_id || ""),
    stock_id: String(item.backend_id || item.id || ""),
    name: item.name || item.title || item.urun_adi || "",
    barcode: item.barcode || "",
    sku: item.sku || "",
    current_stock: TelefonSafeNumber(item.current_stock),
    min_stock: TelefonSafeNumber(item.min_stock),
    sale_price: TelefonSafeNumber(item.sale_price || item.price),
    currency: item.currency || "TRY",
    active: item.active !== false,
  };
}

function TelefonMergeCustomers(...lists) {
  const map = new Map();

  lists.flat().forEach((row) => {
    const customer = TelefonNormalizeCustomer(row, row.customer_source || "customer");
    const phoneKey = TelefonDigits(customer.phone);
    const nameKey = customer.full_name.toLowerCase();
    const key = phoneKey
      ? `phone:${phoneKey}`
      : `name:${nameKey || customer.id || Math.random()}`;

    const existing = map.get(key);

    if (existing) {
      map.set(key, {
        ...existing,
        ...customer,
        full_name: existing.full_name || customer.full_name,
        phone: existing.phone || customer.phone,
        address: existing.address || customer.address,
        apartment: existing.apartment || customer.apartment,
        customer_note: existing.customer_note || customer.customer_note,
        customer_source:
          existing.customer_source === customer.customer_source
            ? existing.customer_source
            : `${existing.customer_source}+${customer.customer_source}`,
      });
      return;
    }

    map.set(key, customer);
  });

  return Array.from(map.values()).filter(
    (c) => c.full_name || c.phone || c.address
  );
}

function TelefonStatusLabel(status, TT) {
  const key = String(status || "new");
  return TT[key] || key;
}

function TelefonPaymentLabel(value, TT) {
  if (value === "card") return TT.card;
  if (value === "bank") return TT.bank;
  if (value === "online") return TT.online;
  return TT.cash;
}

export default function TelefonSiparisPanel({
  mode = "page",
  defaultPhone = "",
  onOrderCreated,
}) {
  const theme = useTheme();

  const [lang, setLang] = useState(TelefonGetLang());
  const TT = TELEFON_TEXT[lang] || TELEFON_TEXT.tr;

  const [tab, setTab] = useState("order");

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [cariCustomers, setCariCustomers] = useState([]);
  const [telefonCustomers, setTelefonCustomers] = useState([]);
  const [stockItems, setStockItems] = useState([]);
  const [telefonOrders, setTelefonOrders] = useState([]);
  const [incomingCalls, setIncomingCalls] = useState([]);

  const [customerForm, setCustomerForm] = useState({
    ...TelefonEmptyCustomer,
    phone: defaultPhone || "",
  });

  const [orderForm, setOrderForm] = useState(TelefonEmptyOrder);
  const [manualItem, setManualItem] = useState(TelefonEmptyManualItem);

  const [productQuery, setProductQuery] = useState("");
  const [customerQuery, setCustomerQuery] = useState(defaultPhone || "");
  const [cariSearch, setCariSearch] = useState("");
  const [orderSearch, setOrderSearch] = useState("");
  const [basket, setBasket] = useState([]);

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "info",
  });

  const notify = useCallback((message, severity = "info") => {
    setSnackbar({
      open: true,
      message,
      severity,
    });
  }, []);

  useEffect(() => {
    const syncLang = () => setLang(TelefonGetLang());

    window.addEventListener("app_lang_change", syncLang);
    window.addEventListener("storage", syncLang);

    return () => {
      window.removeEventListener("app_lang_change", syncLang);
      window.removeEventListener("storage", syncLang);
    };
  }, []);

  const loadTelefonData = useCallback(async () => {
    setLoading(true);

    try {
      const [stockRes, cariRes, phoneCustomersRes, ordersRes, callsRes] =
        await Promise.allSettled([
          TelefonApi("/stock/items"),
          TelefonApi("/cari/customers?limit=1000&offset=0"),
          TelefonApi("/telefon-siparis/customers"),
          TelefonApi("/telefon-siparis/orders?limit=200"),
          TelefonApi("/telefon-siparis/calls/latest"),
        ]);

      if (stockRes.status === "fulfilled") {
        setStockItems(
          (stockRes.value.data || [])
            .map(TelefonNormalizeStock)
            .filter((item) => item.active !== false)
        );
      }

      if (cariRes.status === "fulfilled") {
        setCariCustomers(
          (cariRes.value.data || []).map((row) =>
            TelefonNormalizeCustomer(row, "cari")
          )
        );
      }

      if (phoneCustomersRes.status === "fulfilled") {
        setTelefonCustomers(
          (phoneCustomersRes.value.data || []).map((row) =>
            TelefonNormalizeCustomer(row, "telefon")
          )
        );
      }

      if (ordersRes.status === "fulfilled") {
        setTelefonOrders(Array.isArray(ordersRes.value.data) ? ordersRes.value.data : []);
      }

      if (callsRes.status === "fulfilled") {
        setIncomingCalls(Array.isArray(callsRes.value.data) ? callsRes.value.data : []);
      }
    } catch (err) {
      console.error("Telefon panel veri yükleme hatası:", err);
      notify(TT.serverError, "error");
    } finally {
      setLoading(false);
    }
  }, [TT.serverError, notify]);

  useEffect(() => {
    loadTelefonData();
  }, [loadTelefonData]);

  useEffect(() => {
    if (!defaultPhone) return;

    setCustomerForm((prev) => ({
      ...prev,
      phone: defaultPhone,
    }));

    setCustomerQuery(defaultPhone);
  }, [defaultPhone]);

  const allCustomers = useMemo(() => {
    return TelefonMergeCustomers(cariCustomers, telefonCustomers);
  }, [cariCustomers, telefonCustomers]);

  const matchedCustomers = useMemo(() => {
    const q = String(customerQuery || "").trim().toLowerCase();
    const qDigits = TelefonDigits(q);

    if (!q && !qDigits) return [];

    return allCustomers
      .filter((c) => {
        const haystack = [
          c.full_name,
          c.phone,
          c.address,
          c.city,
          c.customer_source,
        ]
          .join(" ")
          .toLowerCase();

        const phoneDigits = TelefonDigits(c.phone);

        if (qDigits && phoneDigits.includes(qDigits)) return true;
        if (q && haystack.includes(q)) return true;

        return false;
      })
      .slice(0, 8);
  }, [allCustomers, customerQuery]);

  const selectedCustomerExists = useMemo(() => {
    const phone = TelefonDigits(customerForm.phone);

    if (!phone && !customerForm.full_name) return false;

    return cariCustomers.some((c) => {
      const samePhone = phone && TelefonDigits(c.phone) === phone;
      const sameName =
        customerForm.full_name &&
        c.full_name &&
        c.full_name.toLowerCase() === customerForm.full_name.toLowerCase();

      return samePhone || sameName;
    });
  }, [cariCustomers, customerForm.phone, customerForm.full_name]);

  const filteredStockItems = useMemo(() => {
    const q = productQuery.trim().toLowerCase();

    return stockItems
      .filter((item) => {
        if (!q) return true;

        return [item.name, item.barcode, item.sku, item.category_id]
          .join(" ")
          .toLowerCase()
          .includes(q);
      })
      .slice(0, 60);
  }, [stockItems, productQuery]);

  const filteredCariCustomers = useMemo(() => {
    const q = cariSearch.trim().toLowerCase();
    const digits = TelefonDigits(q);

    return cariCustomers.filter((c) => {
      if (!q && !digits) return true;

      const haystack = [
        c.full_name,
        c.phone,
        c.email,
        c.city,
        c.address,
        c.customer_note,
      ]
        .join(" ")
        .toLowerCase();

      if (digits && TelefonDigits(c.phone).includes(digits)) return true;
      return haystack.includes(q);
    });
  }, [cariCustomers, cariSearch]);

  const filteredOrders = useMemo(() => {
    const q = orderSearch.trim().toLowerCase();
    const digits = TelefonDigits(q);

    return telefonOrders.filter((order) => {
      if (!q && !digits) return true;

      const haystack = [
        order.id,
        order.customer_name,
        order.customer_phone,
        order.customer_address,
        order.order_note,
        order.payment_method,
        order.status,
      ]
        .join(" ")
        .toLowerCase();

      if (digits && TelefonDigits(order.customer_phone).includes(digits)) {
        return true;
      }

      return haystack.includes(q);
    });
  }, [telefonOrders, orderSearch]);

  const basketTotal = useMemo(() => {
    return basket.reduce(
      (sum, item) =>
        sum +
        TelefonSafeNumber(item.quantity) * TelefonSafeNumber(item.unit_price),
      0
    );
  }, [basket]);

  const basketCurrency = useMemo(() => {
    return basket.find((x) => x.currency)?.currency || "TRY";
  }, [basket]);

  const stats = useMemo(() => {
    const paid = telefonOrders.filter((x) => x.payment_status === "paid");
    const unpaid = telefonOrders.filter((x) => x.payment_status !== "paid");

    return {
      totalCari: cariCustomers.length,
      totalOrders: telefonOrders.length,
      paidOrders: paid.length,
      unpaidOrders: unpaid.length,
      orderAmount: telefonOrders.reduce(
        (sum, order) => sum + TelefonSafeNumber(order.total_amount),
        0
      ),
    };
  }, [cariCustomers.length, telefonOrders]);

  const selectCustomer = (customer) => {
    setCustomerForm((prev) => ({
      ...prev,
      customer_id: customer.customer_id || customer.id || "",
      customer_source: customer.customer_source || "",
      full_name: customer.full_name || "",
      phone: customer.phone || "",
      address: customer.address || "",
      apartment: customer.apartment || "",
      customer_note: customer.customer_note || "",
    }));

    setCustomerQuery(customer.phone || customer.full_name || "");
  };

  const addStockItemToBasket = (item) => {
    const stockId = String(item.stock_id || item.backend_id || item.id || "");

    setBasket((prev) => {
      const exists = prev.find(
        (row) => row.type === "stock" && String(row.stock_id) === stockId
      );

      if (exists) {
        return prev.map((row) =>
          row.type === "stock" && String(row.stock_id) === stockId
            ? {
                ...row,
                quantity: TelefonSafeNumber(row.quantity) + 1,
              }
            : row
        );
      }

      return [
        ...prev,
        {
          row_id: `TelefonStock-${stockId}-${Date.now()}`,
          type: "stock",
          item_type: "stock",
          stock_id: stockId,
          stock_item_id: stockId,
          item_id: stockId,
          name: item.name || "",
          product_name: item.name || "",
          quantity: 1,
          unit_price: TelefonSafeNumber(item.sale_price),
          currency: item.currency || "TRY",
          current_stock: TelefonSafeNumber(item.current_stock),
          min_stock: TelefonSafeNumber(item.min_stock),
        },
      ];
    });
  };

  const addManualItemToBasket = () => {
    const name = manualItem.name.trim();
    const qty = TelefonSafeNumber(manualItem.quantity);
    const price = TelefonSafeNumber(manualItem.unit_price);

    if (!name || qty <= 0) return;

    setBasket((prev) => [
      ...prev,
      {
        row_id: `TelefonManual-${Date.now()}`,
        type: "manual",
        item_type: "manual",
        stock_id: "",
        stock_item_id: null,
        item_id: "",
        name,
        product_name: name,
        quantity: qty,
        unit_price: price,
        currency: manualItem.currency || "TRY",
      },
    ]);

    setManualItem(TelefonEmptyManualItem);
  };

  const updateBasketQty = (rowId, direction) => {
    setBasket((prev) =>
      prev
        .map((row) => {
          if (row.row_id !== rowId) return row;

          const nextQty =
            direction === "plus"
              ? TelefonSafeNumber(row.quantity) + 1
              : TelefonSafeNumber(row.quantity) - 1;

          return {
            ...row,
            quantity: Math.max(1, nextQty),
          };
        })
        .filter((row) => TelefonSafeNumber(row.quantity) > 0)
    );
  };

  const removeBasketItem = (rowId) => {
    setBasket((prev) => prev.filter((row) => row.row_id !== rowId));
  };

  const resetPanel = () => {
    setCustomerForm(TelefonEmptyCustomer);
    setOrderForm(TelefonEmptyOrder);
    setManualItem(TelefonEmptyManualItem);
    setCustomerQuery("");
    setProductQuery("");
    setBasket([]);
  };

  const ensureCariCustomer = async () => {
    const phoneDigits = TelefonDigits(customerForm.phone);
    const name = customerForm.full_name.trim();

    const existing = cariCustomers.find((c) => {
      const samePhone = phoneDigits && TelefonDigits(c.phone) === phoneDigits;
      const sameName =
        name &&
        c.full_name &&
        c.full_name.trim().toLowerCase() === name.toLowerCase();

      return samePhone || sameName;
    });

    if (existing) {
      notify(TT.cariAlready, "info");
      return existing;
    }

    const payload = {
      full_name: name,
      phone: customerForm.phone,
      email: "",
      gender: "unknown",
      birth_date: null,
      city: "",
      address: customerForm.address || "",
      note: [
        "Telefon siparişinden otomatik oluşturuldu.",
        customerForm.apartment ? `Daire: ${customerForm.apartment}` : "",
        customerForm.customer_note ? `Müşteri notu: ${customerForm.customer_note}` : "",
      ]
        .filter(Boolean)
        .join(" "),
      tags: "Telefon Siparişi",
      source_module: "qr",
      customer_type: "general",
      whatsapp_opt_in: true,
      aktif: true,
    };

    try {
      const result = await TelefonApi("/cari/customers", {
        method: "POST",
        body: JSON.stringify(payload),
      });

      const created = TelefonNormalizeCustomer(
        result.data || result.customer || payload,
        "cari"
      );

      setCariCustomers((prev) =>
        TelefonMergeCustomers([...prev, created])
      );

      notify(TT.cariSaved, "success");
      return created;
    } catch (err) {
      console.warn("Cari oluşturma hatası:", err);

      try {
        const searchResult = await TelefonApi(
          `/cari/customers?limit=1000&offset=0&search=${encodeURIComponent(
            customerForm.phone || name
          )}`
        );

        const rows = (searchResult.data || []).map((row) =>
          TelefonNormalizeCustomer(row, "cari")
        );

        setCariCustomers((prev) => TelefonMergeCustomers(prev, rows));

        const found = rows.find(
          (c) => phoneDigits && TelefonDigits(c.phone) === phoneDigits
        );

        if (found) return found;
      } catch {
        // sessiz geç
      }

      return null;
    }
  };

  const saveTelefonOrder = async () => {
    const phone = TelefonDigits(customerForm.phone);
    const fullName = customerForm.full_name.trim();

    if (!phone) {
      notify(TT.requiredPhone, "warning");
      return;
    }

    if (!fullName) {
      notify(TT.requiredName, "warning");
      return;
    }

    if (!basket.length) {
      notify(TT.requiredBasket, "warning");
      return;
    }

    setSaving(true);

    try {
      const cariCustomer = await ensureCariCustomer();

      const payload = {
        frontend_id: `Telefon-${Date.now()}`,
        source: "phone_order",
        customer_id:
          cariCustomer?.id ||
          customerForm.customer_id ||
          null,
        cari_customer_id: cariCustomer?.id || null,
        customer_source: customerForm.customer_source || "",
        customer_name: fullName,
        customer_phone: customerForm.phone,
        customer_address: customerForm.address,
        customer_apartment: customerForm.apartment,
        customer_note: customerForm.customer_note,
        delivery_type: orderForm.delivery_type,
        table_no: orderForm.delivery_type === "table" ? orderForm.table_no : "",
        payment_status: orderForm.payment_status,
        payment_method: orderForm.payment_method,
        staff_name: orderForm.staff_name,
        order_note: orderForm.order_note,
        allow_negative_stock: orderForm.allow_negative_stock === true,
        items: basket.map((item) => ({
          type: item.type,
          item_type: item.item_type || item.type,
          stock_id: item.stock_id,
          stock_item_id: item.stock_item_id || item.stock_id || null,
          item_id: item.item_id || item.stock_id || null,
          name: item.name,
          product_name: item.product_name || item.name,
          quantity: TelefonSafeNumber(item.quantity),
          unit_price: TelefonSafeNumber(item.unit_price),
          currency: item.currency || basketCurrency,
        })),
        total_amount: basketTotal,
        currency: basketCurrency,
      };

      const result = await TelefonApi("/telefon-siparis/orders", {
        method: "POST",
        body: JSON.stringify(payload),
      });

      const createdOrder = result.data || result.order || payload;

      setTelefonOrders((prev) => [createdOrder, ...prev].slice(0, 200));

      window.dispatchEvent(
        new CustomEvent("telefon_order_created", {
          detail: createdOrder,
        })
      );

      if (typeof onOrderCreated === "function") {
        onOrderCreated(createdOrder);
      }

      notify(TT.savedWithCari, "success");
      resetPanel();
      setTab("orders");
      await loadTelefonData();
    } catch (err) {
      console.error("Telefon siparişi kayıt hatası:", err);
      notify(err.message || TT.serverError, "error");
    } finally {
      setSaving(false);
    }
  };

  const updateOrderStatus = async (order, status) => {
    try {
      const result = await TelefonApi(
        `/telefon-siparis/orders/${order.id}/status`,
        {
          method: "PATCH",
          body: JSON.stringify({ status }),
        }
      );

      const updated = result.data || { ...order, status };

      setTelefonOrders((prev) =>
        prev.map((row) => (String(row.id) === String(order.id) ? updated : row))
      );

      notify(`${TT.status}: ${TelefonStatusLabel(status, TT)}`, "success");
    } catch (err) {
      notify(err.message || TT.serverError, "error");
    }
  };

  const markCallRead = async (call) => {
    try {
      await TelefonApi(`/telefon-siparis/calls/${call.id}/read`, {
        method: "PATCH",
      });

      setIncomingCalls((prev) =>
        prev.filter((row) => String(row.id) !== String(call.id))
      );

      notify(TT.markRead, "success");
    } catch (err) {
      notify(err.message || TT.serverError, "error");
    }
  };

  const fillFromCall = (call) => {
    setTab("order");

    setCustomerForm((prev) => ({
      ...prev,
      customer_id: call.customer_id || "",
      full_name: call.customer_name || prev.full_name || "",
      phone: call.phone || "",
      address: call.customer_address || prev.address || "",
    }));

    setCustomerQuery(call.phone || call.customer_name || "");
  };

  const panelCardSx = {
    borderRadius: 4,
    border: `1px solid ${BRAND.border}`,
    boxShadow: "0 18px 50px rgba(15,23,42,0.08)",
    overflow: "hidden",
    animation: "telefonFadeUp .42s ease both",
  };

  const inputSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: 3,
      bgcolor: "#fff",
    },
  };

  const deliveryOptions = [
    {
      value: "delivery",
      label: TT.delivery,
      icon: <LocalShippingIcon fontSize="small" />,
    },
    {
      value: "pickup",
      label: TT.pickup,
      icon: <StorefrontIcon fontSize="small" />,
    },
    {
      value: "table",
      label: TT.table,
      icon: <TableRestaurantIcon fontSize="small" />,
    },
  ];

  return (
    <Box
      sx={{
        width: "100%",
        minWidth: 0,
        p:
          mode === "dialog"
            ? { xs: 1.5, sm: 2 }
            : { xs: 1.5, sm: 2.5, md: 3 },
        bgcolor: BRAND.bg,
      }}
    >
      <style>
        {`
          @keyframes telefonFadeUp {
            from {
              opacity: 0;
              transform: translateY(18px) scale(.985);
            }
            to {
              opacity: 1;
              transform: translateY(0) scale(1);
            }
          }

          @keyframes telefonPulse {
            0% {
              transform: scale(1);
              box-shadow: 0 0 0 0 rgba(245,130,32,.32);
            }
            70% {
              transform: scale(1.035);
              box-shadow: 0 0 0 14px rgba(245,130,32,0);
            }
            100% {
              transform: scale(1);
              box-shadow: 0 0 0 0 rgba(245,130,32,0);
            }
          }

          @keyframes telefonRing {
            0%,100% { transform: rotate(0deg); }
            15% { transform: rotate(-10deg); }
            30% { transform: rotate(10deg); }
            45% { transform: rotate(-8deg); }
            60% { transform: rotate(8deg); }
            75% { transform: rotate(-4deg); }
          }

          @keyframes telefonShine {
            0% { transform: translateX(-120%); }
            100% { transform: translateX(160%); }
          }
        `}
      </style>

      <Card
        sx={{
          ...panelCardSx,
          mb: 2.5,
          position: "relative",
          background:
            "linear-gradient(135deg, #0B4D8B 0%, #1565C0 48%, #F58220 100%)",
          color: "#fff",
          "&:before": {
            content: '""',
            position: "absolute",
            inset: 0,
            width: "45%",
            background:
              "linear-gradient(90deg, transparent, rgba(255,255,255,.22), transparent)",
            animation: "telefonShine 4.5s ease-in-out infinite",
            pointerEvents: "none",
          },
        }}
      >
        <CardContent sx={{ p: { xs: 2, md: 3 }, position: "relative" }}>
          <Stack
            direction={{ xs: "column", md: "row" }}
            alignItems={{ xs: "flex-start", md: "center" }}
            justifyContent="space-between"
            spacing={2}
          >
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Box
                sx={{
                  width: 58,
                  height: 58,
                  borderRadius: 4,
                  display: "grid",
                  placeItems: "center",
                  bgcolor: "rgba(255,255,255,0.16)",
                  border: "1px solid rgba(255,255,255,0.24)",
                  animation: "telefonPulse 2.8s infinite",
                }}
              >
                <PhoneInTalkIcon
                  sx={{
                    fontSize: 34,
                    animation: "telefonRing 2.5s ease-in-out infinite",
                    transformOrigin: "50% 50%",
                  }}
                />
              </Box>

              <Box sx={{ minWidth: 0 }}>
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 950,
                    letterSpacing: "-0.02em",
                    fontSize: { xs: "1.35rem", md: "1.9rem" },
                  }}
                >
                  {TT.title}
                </Typography>

                <Typography
                  variant="body2"
                  sx={{
                    opacity: 0.92,
                    maxWidth: 860,
                    fontSize: { xs: "0.86rem", md: "0.96rem" },
                  }}
                >
                  {TT.subtitle}
                </Typography>
              </Box>
            </Stack>

            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
              <Chip
                icon={<CheckCircleIcon />}
                label={TT.integrationReady}
                sx={{
                  bgcolor: "rgba(255,255,255,0.18)",
                  color: "#fff",
                  fontWeight: 900,
                  border: "1px solid rgba(255,255,255,0.24)",
                }}
              />

              <Button
                startIcon={
                  loading ? (
                    <CircularProgress size={16} sx={{ color: "#fff" }} />
                  ) : (
                    <RefreshIcon />
                  )
                }
                onClick={loadTelefonData}
                disabled={loading}
                sx={{
                  borderRadius: 999,
                  bgcolor: "rgba(255,255,255,0.16)",
                  color: "#fff",
                  fontWeight: 900,
                  px: 2,
                  "&:hover": {
                    bgcolor: "rgba(255,255,255,0.24)",
                  },
                }}
              >
                {TT.refresh}
              </Button>
            </Stack>
          </Stack>
        </CardContent>
      </Card>

      <Grid container spacing={1.5} sx={{ mb: 2.2 }}>
        <Grid item xs={6} md={3}>
          <StatCard
            icon={<PeopleAltIcon />}
            title={TT.totalCari}
            value={stats.totalCari}
            color={BRAND.blue}
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatCard
            icon={<ReceiptLongIcon />}
            title={TT.totalOrders}
            value={stats.totalOrders}
            color={BRAND.orange}
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatCard
            icon={<DoneAllIcon />}
            title={TT.paidOrders}
            value={stats.paidOrders}
            color={BRAND.success}
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatCard
            icon={<AccountBalanceWalletIcon />}
            title={TT.unpaidOrders}
            value={stats.unpaidOrders}
            color={BRAND.warning}
          />
        </Grid>
      </Grid>

      <Card sx={{ ...panelCardSx, mb: 2.2 }}>
        <Box
          sx={{
            px: { xs: 1, sm: 2 },
            bgcolor: "#fff",
            overflowX: "auto",
          }}
        >
          <Tabs
            value={tab}
            onChange={(e, value) => setTab(value)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              minHeight: 58,
              "& .MuiTab-root": {
                minHeight: 58,
                fontWeight: 950,
                textTransform: "none",
              },
              "& .Mui-selected": {
                color: `${BRAND.orange} !important`,
              },
              "& .MuiTabs-indicator": {
                height: 4,
                borderRadius: 999,
                bgcolor: BRAND.orange,
              },
            }}
          >
            <Tab
              value="order"
              icon={<PhoneInTalkIcon />}
              iconPosition="start"
              label={TT.tabOrder}
            />
            <Tab
              value="cari"
              icon={<PeopleAltIcon />}
              iconPosition="start"
              label={TT.tabCari}
            />
            <Tab
              value="orders"
              icon={<HistoryIcon />}
              iconPosition="start"
              label={TT.tabOrders}
            />
            <Tab
              value="calls"
              icon={<CallReceivedIcon />}
              iconPosition="start"
              label={TT.tabCalls}
            />
          </Tabs>
        </Box>
      </Card>

      {tab === "order" && (
        <Grid container spacing={2.2}>
          <Grid item xs={12} lg={4}>
            <Stack spacing={2.2}>
              <Card sx={panelCardSx}>
                <CardContent sx={{ p: { xs: 2, md: 2.4 } }}>
                  <Stack direction="row" spacing={1.2} alignItems="center" mb={1.5}>
                    <LocalPhoneIcon sx={{ color: BRAND.orange }} />
                    <Box>
                      <Typography fontWeight={950}>{TT.incomingCall}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {TT.incomingCallText}
                      </Typography>
                    </Box>
                  </Stack>

                  <TextField
                    fullWidth
                    label={TT.phone}
                    placeholder={TT.phonePlaceholder}
                    value={customerForm.phone}
                    onChange={(e) => {
                      setCustomerForm((prev) => ({
                        ...prev,
                        phone: e.target.value,
                      }));
                      setCustomerQuery(e.target.value);
                    }}
                    sx={inputSx}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <PhoneInTalkIcon fontSize="small" />
                        </InputAdornment>
                      ),
                    }}
                  />

                  <Alert
                    severity={selectedCustomerExists ? "success" : "info"}
                    sx={{ mt: 1.5, borderRadius: 3 }}
                  >
                    {selectedCustomerExists ? TT.foundCustomer : TT.newCustomer}
                  </Alert>
                </CardContent>
              </Card>

              <Card sx={panelCardSx}>
                <CardContent sx={{ p: { xs: 2, md: 2.4 } }}>
                  <Stack direction="row" spacing={1.2} alignItems="center" mb={1.5}>
                    <PersonSearchIcon sx={{ color: BRAND.blue }} />
                    <Typography fontWeight={950}>{TT.customerInfo}</Typography>
                  </Stack>

                  <Stack spacing={1.4}>
                    <TextField
                      fullWidth
                      label={TT.findCustomer}
                      value={customerQuery}
                      onChange={(e) => setCustomerQuery(e.target.value)}
                      sx={inputSx}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon fontSize="small" />
                          </InputAdornment>
                        ),
                      }}
                    />

                    {matchedCustomers.length > 0 && (
                      <Box
                        sx={{
                          borderRadius: 3,
                          border: `1px solid ${BRAND.border}`,
                          overflow: "hidden",
                          bgcolor: "#fff",
                        }}
                      >
                        <Box
                          sx={{
                            px: 1.4,
                            py: 1,
                            bgcolor: BRAND.softBlue,
                            fontWeight: 900,
                            fontSize: "0.82rem",
                          }}
                        >
                          {TT.customerResults}
                        </Box>

                        <Stack divider={<Divider />}>
                          {matchedCustomers.map((customer, index) => (
                            <Box
                              key={`${customer.customer_source}-${customer.customer_id}-${index}`}
                              sx={{
                                p: 1.2,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                gap: 1,
                                minWidth: 0,
                                animation: "telefonFadeUp .35s ease both",
                              }}
                            >
                              <Box sx={{ minWidth: 0 }}>
                                <Stack
                                  direction="row"
                                  spacing={0.7}
                                  alignItems="center"
                                  sx={{ minWidth: 0 }}
                                >
                                  <Typography
                                    variant="body2"
                                    fontWeight={900}
                                    noWrap
                                  >
                                    {customer.full_name || "-"}
                                  </Typography>
                                  <Chip
                                    size="small"
                                    label={
                                      customer.customer_source?.includes("cari")
                                        ? TT.sourceCari
                                        : customer.customer_source?.includes("beauty")
                                        ? TT.sourceBeauty
                                        : TT.sourceTelefon
                                    }
                                    sx={{
                                      height: 20,
                                      fontSize: 11,
                                      fontWeight: 900,
                                      bgcolor: BRAND.softOrange,
                                    }}
                                  />
                                </Stack>

                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  noWrap
                                  display="block"
                                >
                                  {customer.phone || "-"}
                                </Typography>

                                {customer.address && (
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    noWrap
                                    display="block"
                                  >
                                    {customer.address}
                                  </Typography>
                                )}
                              </Box>

                              <Button
                                size="small"
                                variant="contained"
                                onClick={() => selectCustomer(customer)}
                                sx={{
                                  borderRadius: 999,
                                  bgcolor: BRAND.blue,
                                  fontWeight: 900,
                                  flexShrink: 0,
                                }}
                              >
                                {TT.select}
                              </Button>
                            </Box>
                          ))}
                        </Stack>
                      </Box>
                    )}

                    <TextField
                      fullWidth
                      label={TT.customerName}
                      value={customerForm.full_name}
                      onChange={(e) =>
                        setCustomerForm((prev) => ({
                          ...prev,
                          full_name: e.target.value,
                        }))
                      }
                      sx={inputSx}
                    />

                    <TextField
                      fullWidth
                      label={TT.address}
                      multiline
                      minRows={2}
                      value={customerForm.address}
                      onChange={(e) =>
                        setCustomerForm((prev) => ({
                          ...prev,
                          address: e.target.value,
                        }))
                      }
                      sx={inputSx}
                    />

                    <TextField
                      fullWidth
                      label={TT.apartment}
                      value={customerForm.apartment}
                      onChange={(e) =>
                        setCustomerForm((prev) => ({
                          ...prev,
                          apartment: e.target.value,
                        }))
                      }
                      sx={inputSx}
                    />

                    <TextField
                      fullWidth
                      label={TT.customerNote}
                      value={customerForm.customer_note}
                      onChange={(e) =>
                        setCustomerForm((prev) => ({
                          ...prev,
                          customer_note: e.target.value,
                        }))
                      }
                      sx={inputSx}
                    />
                  </Stack>
                </CardContent>
              </Card>

              <Card sx={panelCardSx}>
                <CardContent sx={{ p: { xs: 2, md: 2.4 } }}>
                  <Stack direction="row" spacing={1.2} alignItems="center" mb={1.5}>
                    <PaymentsIcon sx={{ color: BRAND.orange }} />
                    <Typography fontWeight={950}>{TT.deliveryType}</Typography>
                  </Stack>

                  <Stack spacing={1.4}>
                    <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                      {deliveryOptions.map((opt) => (
                        <Button
                          key={opt.value}
                          startIcon={opt.icon}
                          onClick={() =>
                            setOrderForm((prev) => ({
                              ...prev,
                              delivery_type: opt.value,
                            }))
                          }
                          variant={
                            orderForm.delivery_type === opt.value
                              ? "contained"
                              : "outlined"
                          }
                          sx={{
                            borderRadius: 999,
                            fontWeight: 900,
                            bgcolor:
                              orderForm.delivery_type === opt.value
                                ? BRAND.blue
                                : "#fff",
                          }}
                        >
                          {opt.label}
                        </Button>
                      ))}
                    </Stack>

                    {orderForm.delivery_type === "table" && (
                      <TextField
                        fullWidth
                        label={TT.tableNo}
                        value={orderForm.table_no}
                        onChange={(e) =>
                          setOrderForm((prev) => ({
                            ...prev,
                            table_no: e.target.value,
                          }))
                        }
                        sx={inputSx}
                      />
                    )}

                    <Grid container spacing={1.2}>
                      <Grid item xs={12} sm={6}>
                        <Select
                          fullWidth
                          value={orderForm.payment_status}
                          onChange={(e) =>
                            setOrderForm((prev) => ({
                              ...prev,
                              payment_status: e.target.value,
                            }))
                          }
                          sx={{
                            borderRadius: 3,
                            bgcolor: "#fff",
                          }}
                        >
                          <MenuItem value="unpaid">{TT.unpaid}</MenuItem>
                          <MenuItem value="paid">{TT.paid}</MenuItem>
                        </Select>
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <Select
                          fullWidth
                          value={orderForm.payment_method}
                          onChange={(e) =>
                            setOrderForm((prev) => ({
                              ...prev,
                              payment_method: e.target.value,
                            }))
                          }
                          sx={{
                            borderRadius: 3,
                            bgcolor: "#fff",
                          }}
                        >
                          <MenuItem value="cash">{TT.cash}</MenuItem>
                          <MenuItem value="card">{TT.card}</MenuItem>
                          <MenuItem value="bank">{TT.bank}</MenuItem>
                          <MenuItem value="online">{TT.online}</MenuItem>
                        </Select>
                      </Grid>
                    </Grid>

                    <TextField
                      fullWidth
                      label={TT.staff}
                      value={orderForm.staff_name}
                      onChange={(e) =>
                        setOrderForm((prev) => ({
                          ...prev,
                          staff_name: e.target.value,
                        }))
                      }
                      sx={inputSx}
                    />

                    <TextField
                      fullWidth
                      label={TT.orderNote}
                      multiline
                      minRows={2}
                      value={orderForm.order_note}
                      onChange={(e) =>
                        setOrderForm((prev) => ({
                          ...prev,
                          order_note: e.target.value,
                        }))
                      }
                      sx={inputSx}
                    />

                    <Button
                      variant={
                        orderForm.allow_negative_stock ? "contained" : "outlined"
                      }
                      color={orderForm.allow_negative_stock ? "warning" : "inherit"}
                      onClick={() =>
                        setOrderForm((prev) => ({
                          ...prev,
                          allow_negative_stock: !prev.allow_negative_stock,
                        }))
                      }
                      sx={{
                        borderRadius: 3,
                        fontWeight: 900,
                        justifyContent: "flex-start",
                      }}
                    >
                      {TT.allowNegativeStock}
                    </Button>
                  </Stack>
                </CardContent>
              </Card>
            </Stack>
          </Grid>

          <Grid item xs={12} lg={4}>
            <Stack spacing={2.2}>
              <Card sx={panelCardSx}>
                <CardContent sx={{ p: { xs: 2, md: 2.4 } }}>
                  <Stack direction="row" spacing={1.2} alignItems="center" mb={1.5}>
                    <InventoryIcon sx={{ color: BRAND.blue }} />
                    <Box sx={{ minWidth: 0 }}>
                      <Typography fontWeight={950}>{TT.stockProducts}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {stockItems.length} {TT.piece}
                      </Typography>
                    </Box>
                  </Stack>

                  <TextField
                    fullWidth
                    label={TT.productSearch}
                    placeholder={TT.productSearchPlaceholder}
                    value={productQuery}
                    onChange={(e) => setProductQuery(e.target.value)}
                    sx={{ ...inputSx, mb: 1.5 }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon fontSize="small" />
                        </InputAdornment>
                      ),
                    }}
                  />

                  <Stack
                    spacing={1}
                    sx={{
                      maxHeight: { xs: 360, lg: 625 },
                      overflowY: "auto",
                      pr: 0.5,
                    }}
                  >
                    {loading && (
                      <Box sx={{ textAlign: "center", py: 4 }}>
                        <CircularProgress size={28} />
                      </Box>
                    )}

                    {!loading &&
                      filteredStockItems.map((item) => {
                        const isCritical =
                          item.current_stock <= item.min_stock &&
                          item.current_stock > 0;
                        const isOut = item.current_stock <= 0;

                        return (
                          <Box
                            key={item.stock_id || item.id}
                            sx={{
                              p: 1.3,
                              borderRadius: 3,
                              bgcolor: "#fff",
                              border: `1px solid ${
                                isOut
                                  ? alpha(theme.palette.error.main, 0.32)
                                  : isCritical
                                  ? alpha(theme.palette.warning.main, 0.32)
                                  : BRAND.border
                              }`,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              gap: 1.2,
                              minWidth: 0,
                              transition: "all .22s ease",
                              animation: "telefonFadeUp .35s ease both",
                              "&:hover": {
                                transform: "translateY(-2px)",
                                boxShadow: "0 12px 28px rgba(15,23,42,.10)",
                              },
                            }}
                          >
                            <Box sx={{ minWidth: 0 }}>
                              <Typography
                                variant="body2"
                                fontWeight={950}
                                noWrap
                              >
                                {item.name || "-"}
                              </Typography>

                              <Stack
                                direction="row"
                                spacing={0.6}
                                useFlexGap
                                flexWrap="wrap"
                                sx={{ mt: 0.5 }}
                              >
                                <Chip
                                  size="small"
                                  label={`${TT.stockCount}: ${item.current_stock}`}
                                  color={
                                    isOut
                                      ? "error"
                                      : isCritical
                                      ? "warning"
                                      : "default"
                                  }
                                />
                                <Chip
                                  size="small"
                                  label={TelefonMoney(
                                    item.sale_price,
                                    item.currency,
                                    lang
                                  )}
                                  sx={{
                                    fontWeight: 900,
                                    bgcolor: BRAND.softOrange,
                                  }}
                                />
                              </Stack>

                              {(isCritical || isOut) && (
                                <Typography
                                  variant="caption"
                                  color={isOut ? "error" : "warning.main"}
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 0.4,
                                    mt: 0.5,
                                    fontWeight: 800,
                                  }}
                                >
                                  <WarningAmberIcon sx={{ fontSize: 15 }} />
                                  {isOut ? TT.outOfStock : TT.criticalStock}
                                </Typography>
                              )}
                            </Box>

                            <Button
                              size="small"
                              variant="contained"
                              startIcon={<AddIcon />}
                              onClick={() => addStockItemToBasket(item)}
                              disabled={isOut && !orderForm.allow_negative_stock}
                              sx={{
                                borderRadius: 999,
                                bgcolor: BRAND.orange,
                                fontWeight: 950,
                                flexShrink: 0,
                              }}
                            >
                              {TT.add}
                            </Button>
                          </Box>
                        );
                      })}
                  </Stack>
                </CardContent>
              </Card>

              <Card sx={panelCardSx}>
                <CardContent sx={{ p: { xs: 2, md: 2.4 } }}>
                  <Typography fontWeight={950} mb={0.5}>
                    {TT.manualItem}
                  </Typography>

                  <Typography variant="caption" color="text.secondary">
                    {TT.manualItemText}
                  </Typography>

                  <Stack spacing={1.2} mt={1.5}>
                    <TextField
                      fullWidth
                      label={TT.itemName}
                      value={manualItem.name}
                      onChange={(e) =>
                        setManualItem((prev) => ({
                          ...prev,
                          name: e.target.value,
                        }))
                      }
                      sx={inputSx}
                    />

                    <Grid container spacing={1.2}>
                      <Grid item xs={4}>
                        <TextField
                          fullWidth
                          label={TT.quantity}
                          type="number"
                          value={manualItem.quantity}
                          onChange={(e) =>
                            setManualItem((prev) => ({
                              ...prev,
                              quantity: e.target.value,
                            }))
                          }
                          sx={inputSx}
                        />
                      </Grid>

                      <Grid item xs={4}>
                        <TextField
                          fullWidth
                          label={TT.unitPrice}
                          type="number"
                          value={manualItem.unit_price}
                          onChange={(e) =>
                            setManualItem((prev) => ({
                              ...prev,
                              unit_price: e.target.value,
                            }))
                          }
                          sx={inputSx}
                        />
                      </Grid>

                      <Grid item xs={4}>
                        <TextField
                          fullWidth
                          label={TT.currency}
                          value={manualItem.currency}
                          onChange={(e) =>
                            setManualItem((prev) => ({
                              ...prev,
                              currency: e.target.value.toUpperCase(),
                            }))
                          }
                          sx={inputSx}
                        />
                      </Grid>
                    </Grid>

                    <Button
                      fullWidth
                      variant="outlined"
                      startIcon={<AddIcon />}
                      onClick={addManualItemToBasket}
                      sx={{
                        borderRadius: 3,
                        fontWeight: 950,
                        borderColor: BRAND.orange,
                        color: BRAND.orange,
                      }}
                    >
                      {TT.addManualItem}
                    </Button>
                  </Stack>
                </CardContent>
              </Card>
            </Stack>
          </Grid>

          <Grid item xs={12} lg={4}>
            <Stack spacing={2.2}>
              <Card sx={panelCardSx}>
                <CardContent sx={{ p: { xs: 2, md: 2.4 } }}>
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    spacing={1}
                    mb={1.5}
                  >
                    <Stack direction="row" spacing={1.2} alignItems="center">
                      <ShoppingCartIcon sx={{ color: BRAND.orange }} />
                      <Typography fontWeight={950}>{TT.basket}</Typography>
                    </Stack>

                    <Chip
                      label={TelefonMoney(basketTotal, basketCurrency, lang)}
                      sx={{
                        fontWeight: 950,
                        bgcolor: BRAND.softOrange,
                        color: BRAND.dark,
                      }}
                    />
                  </Stack>

                  {basket.length === 0 ? (
                    <Alert severity="info" sx={{ borderRadius: 3 }}>
                      {TT.emptyBasket}
                    </Alert>
                  ) : (
                    <Stack spacing={1}>
                      {basket.map((item) => (
                        <Box
                          key={item.row_id}
                          sx={{
                            p: 1.2,
                            borderRadius: 3,
                            border: `1px solid ${BRAND.border}`,
                            bgcolor: "#fff",
                            animation: "telefonFadeUp .3s ease both",
                          }}
                        >
                          <Stack
                            direction="row"
                            alignItems="flex-start"
                            justifyContent="space-between"
                            spacing={1}
                          >
                            <Box sx={{ minWidth: 0 }}>
                              <Typography
                                variant="body2"
                                fontWeight={950}
                                noWrap
                              >
                                {item.name}
                              </Typography>

                              <Typography variant="caption" color="text.secondary">
                                {item.type === "stock"
                                  ? TT.stockProducts
                                  : TT.manualItem}
                              </Typography>
                            </Box>

                            <Tooltip title={TT.remove}>
                              <IconButton
                                size="small"
                                onClick={() => removeBasketItem(item.row_id)}
                                sx={{
                                  color: BRAND.danger,
                                  flexShrink: 0,
                                }}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Stack>

                          <Stack
                            direction="row"
                            alignItems="center"
                            justifyContent="space-between"
                            spacing={1}
                            mt={1}
                          >
                            <Stack direction="row" alignItems="center" spacing={0.5}>
                              <IconButton
                                size="small"
                                onClick={() =>
                                  updateBasketQty(item.row_id, "minus")
                                }
                                sx={{
                                  border: `1px solid ${BRAND.border}`,
                                }}
                              >
                                <RemoveIcon fontSize="small" />
                              </IconButton>

                              <Typography
                                sx={{
                                  minWidth: 34,
                                  textAlign: "center",
                                  fontWeight: 950,
                                }}
                              >
                                {item.quantity}
                              </Typography>

                              <IconButton
                                size="small"
                                onClick={() => updateBasketQty(item.row_id, "plus")}
                                sx={{
                                  border: `1px solid ${BRAND.border}`,
                                }}
                              >
                                <AddIcon fontSize="small" />
                              </IconButton>
                            </Stack>

                            <Typography fontWeight={950}>
                              {TelefonMoney(
                                TelefonSafeNumber(item.quantity) *
                                  TelefonSafeNumber(item.unit_price),
                                item.currency,
                                lang
                              )}
                            </Typography>
                          </Stack>
                        </Box>
                      ))}
                    </Stack>
                  )}

                  <Divider sx={{ my: 2 }} />

                  <Stack spacing={1.2}>
                    <Box
                      sx={{
                        p: 1.5,
                        borderRadius: 3,
                        bgcolor: BRAND.softBlue,
                        border: `1px solid ${alpha(BRAND.blue, 0.14)}`,
                      }}
                    >
                      <Typography variant="caption" color="text.secondary">
                        {TT.selectedCustomer}
                      </Typography>
                      <Typography fontWeight={950}>
                        {customerForm.full_name || "-"}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {customerForm.phone || "-"}
                      </Typography>
                    </Box>

                    <Box
                      sx={{
                        p: 1.5,
                        borderRadius: 3,
                        bgcolor: BRAND.softOrange,
                        border: `1px solid ${alpha(BRAND.orange, 0.16)}`,
                      }}
                    >
                      <Typography variant="caption" color="text.secondary">
                        {TT.total}
                      </Typography>
                      <Typography
                        sx={{
                          fontWeight: 950,
                          fontSize: "1.35rem",
                          color: BRAND.dark,
                        }}
                      >
                        {TelefonMoney(basketTotal, basketCurrency, lang)}
                      </Typography>
                    </Box>

                    <Alert severity="success" sx={{ borderRadius: 3 }}>
                      {TT.addToCariInfo}
                    </Alert>

                    <Button
                      fullWidth
                      variant="contained"
                      size="large"
                      startIcon={
                        saving ? (
                          <CircularProgress size={18} sx={{ color: "#fff" }} />
                        ) : (
                          <SaveIcon />
                        )
                      }
                      onClick={saveTelefonOrder}
                      disabled={saving}
                      sx={{
                        borderRadius: 3,
                        py: 1.35,
                        fontWeight: 950,
                        bgcolor: BRAND.blue,
                        boxShadow: "0 14px 30px rgba(11,77,139,0.22)",
                        "&:hover": {
                          bgcolor: BRAND.blue2,
                        },
                      }}
                    >
                      {saving ? TT.saving : TT.completeOrder}
                    </Button>

                    <Button
                      fullWidth
                      variant="outlined"
                      onClick={resetPanel}
                      sx={{
                        borderRadius: 3,
                        fontWeight: 900,
                      }}
                    >
                      {TT.reset}
                    </Button>
                  </Stack>
                </CardContent>
              </Card>
            </Stack>
          </Grid>
        </Grid>
      )}

      {tab === "cari" && (
        <Card sx={panelCardSx}>
          <CardContent sx={{ p: { xs: 2, md: 2.6 } }}>
            <Stack
              direction={{ xs: "column", md: "row" }}
              spacing={1.5}
              alignItems={{ xs: "stretch", md: "center" }}
              justifyContent="space-between"
              mb={2}
            >
              <Stack direction="row" spacing={1.2} alignItems="center">
                <PeopleAltIcon sx={{ color: BRAND.blue }} />
                <Box>
                  <Typography fontWeight={950}>{TT.cariList}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {filteredCariCustomers.length} / {cariCustomers.length}
                  </Typography>
                </Box>
              </Stack>

              <TextField
                value={cariSearch}
                onChange={(e) => setCariSearch(e.target.value)}
                placeholder={TT.cariSearch}
                sx={{
                  ...inputSx,
                  width: { xs: "100%", md: 420 },
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
            </Stack>

            {filteredCariCustomers.length === 0 ? (
              <Alert severity="info" sx={{ borderRadius: 3 }}>
                {TT.noCari}
              </Alert>
            ) : (
              <Grid container spacing={1.4}>
                {filteredCariCustomers.map((customer, index) => (
                  <Grid item xs={12} sm={6} lg={4} key={`${customer.id}-${index}`}>
                    <Box
                      sx={{
                        p: 1.6,
                        borderRadius: 4,
                        bgcolor: "#fff",
                        border: `1px solid ${BRAND.border}`,
                        height: "100%",
                        cursor: "pointer",
                        animation: "telefonFadeUp .36s ease both",
                        transition: "all .22s ease",
                        "&:hover": {
                          transform: "translateY(-3px)",
                          boxShadow: "0 16px 36px rgba(15,23,42,.10)",
                          borderColor: alpha(BRAND.blue, 0.32),
                        },
                      }}
                      onClick={() => {
                        selectCustomer(customer);
                        setTab("order");
                      }}
                    >
                      <Stack
                        direction="row"
                        justifyContent="space-between"
                        spacing={1}
                        alignItems="flex-start"
                      >
                        <Box sx={{ minWidth: 0 }}>
                          <Typography fontWeight={950} noWrap>
                            {customer.full_name || "-"}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" noWrap>
                            {customer.phone || "-"}
                          </Typography>
                        </Box>

                        <Chip
                          size="small"
                          label={customer.aktif === false ? TT.passive : TT.active}
                          sx={{
                            bgcolor:
                              customer.aktif === false ? "#FEF3F2" : "#ECFDF3",
                            color:
                              customer.aktif === false ? BRAND.danger : "#027A48",
                            fontWeight: 900,
                          }}
                        />
                      </Stack>

                      <Divider sx={{ my: 1.2 }} />

                      <Typography variant="caption" color="text.secondary">
                        {TT.addressShort}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          color: BRAND.ink,
                          minHeight: 42,
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                        }}
                      >
                        {customer.address || "-"}
                      </Typography>

                      <Stack direction="row" spacing={0.7} mt={1.2} flexWrap="wrap">
                        <Chip
                          size="small"
                          label={TT.sourceCari}
                          sx={{ bgcolor: BRAND.softBlue, fontWeight: 900 }}
                        />
                        {customer.city && (
                          <Chip
                            size="small"
                            label={customer.city}
                            sx={{ bgcolor: BRAND.softOrange, fontWeight: 900 }}
                          />
                        )}
                      </Stack>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            )}
          </CardContent>
        </Card>
      )}

      {tab === "orders" && (
        <Card sx={panelCardSx}>
          <CardContent sx={{ p: { xs: 2, md: 2.6 } }}>
            <Stack
              direction={{ xs: "column", md: "row" }}
              spacing={1.5}
              alignItems={{ xs: "stretch", md: "center" }}
              justifyContent="space-between"
              mb={2}
            >
              <Stack direction="row" spacing={1.2} alignItems="center">
                <HistoryIcon sx={{ color: BRAND.orange }} />
                <Box>
                  <Typography fontWeight={950}>{TT.orderHistory}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {filteredOrders.length} / {telefonOrders.length}
                  </Typography>
                </Box>
              </Stack>

              <TextField
                value={orderSearch}
                onChange={(e) => setOrderSearch(e.target.value)}
                placeholder={TT.findCustomer}
                sx={{
                  ...inputSx,
                  width: { xs: "100%", md: 420 },
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
            </Stack>

            {filteredOrders.length === 0 ? (
              <Alert severity="info" sx={{ borderRadius: 3 }}>
                {TT.noOrder}
              </Alert>
            ) : (
              <Stack spacing={1.2}>
                {filteredOrders.map((order) => (
                  <Box
                    key={order.id}
                    sx={{
                      p: { xs: 1.4, md: 1.6 },
                      borderRadius: 4,
                      bgcolor: "#fff",
                      border: `1px solid ${BRAND.border}`,
                      animation: "telefonFadeUp .34s ease both",
                    }}
                  >
                    <Stack
                      direction={{ xs: "column", md: "row" }}
                      justifyContent="space-between"
                      spacing={1.5}
                    >
                      <Box sx={{ minWidth: 0 }}>
                        <Stack
                          direction="row"
                          spacing={0.8}
                          alignItems="center"
                          flexWrap="wrap"
                        >
                          <Typography fontWeight={950}>
                            #{order.id} · {order.customer_name || "-"}
                          </Typography>

                          <Chip
                            size="small"
                            label={TelefonStatusLabel(order.status, TT)}
                            sx={{
                              bgcolor:
                                order.status === "completed"
                                  ? "#ECFDF3"
                                  : order.status === "cancelled"
                                  ? "#FEF3F2"
                                  : BRAND.softOrange,
                              color:
                                order.status === "completed"
                                  ? "#027A48"
                                  : order.status === "cancelled"
                                  ? BRAND.danger
                                  : BRAND.dark,
                              fontWeight: 900,
                            }}
                          />

                          <Chip
                            size="small"
                            label={
                              order.payment_status === "paid"
                                ? TT.paid
                                : TT.unpaid
                            }
                            sx={{
                              bgcolor:
                                order.payment_status === "paid"
                                  ? "#ECFDF3"
                                  : "#FFF7ED",
                              fontWeight: 900,
                            }}
                          />
                        </Stack>

                        <Typography variant="body2" color="text.secondary">
                          {order.customer_phone || "-"} ·{" "}
                          {TelefonPaymentLabel(order.payment_method, TT)}
                        </Typography>

                        {order.customer_address && (
                          <Typography variant="body2" color="text.secondary">
                            {order.customer_address}
                          </Typography>
                        )}

                        {Array.isArray(order.items) && order.items.length > 0 && (
                          <Typography variant="caption" color="text.secondary">
                            {order.items
                              .map(
                                (item) =>
                                  `${item.product_name || item.name || "-"} x ${
                                    item.quantity
                                  }`
                              )
                              .join(", ")}
                          </Typography>
                        )}
                      </Box>

                      <Stack
                        spacing={1}
                        alignItems={{ xs: "stretch", md: "flex-end" }}
                        sx={{ minWidth: { xs: "100%", md: 220 } }}
                      >
                        <Typography
                          sx={{
                            fontWeight: 950,
                            fontSize: "1.15rem",
                            color: BRAND.dark,
                            textAlign: { xs: "left", md: "right" },
                          }}
                        >
                          {TelefonMoney(order.total_amount, order.currency, lang)}
                        </Typography>

                        <Stack direction="row" spacing={0.7} flexWrap="wrap">
                          {order.status !== "preparing" &&
                            order.status !== "completed" &&
                            order.status !== "cancelled" && (
                              <Button
                                size="small"
                                variant="outlined"
                                onClick={() => updateOrderStatus(order, "preparing")}
                                sx={{
                                  borderRadius: 999,
                                  fontWeight: 900,
                                }}
                              >
                                {TT.markPreparing}
                              </Button>
                            )}

                          {order.status !== "completed" &&
                            order.status !== "cancelled" && (
                              <Button
                                size="small"
                                variant="contained"
                                onClick={() => updateOrderStatus(order, "completed")}
                                sx={{
                                  borderRadius: 999,
                                  bgcolor: BRAND.success,
                                  fontWeight: 900,
                                }}
                              >
                                {TT.markCompleted}
                              </Button>
                            )}
                        </Stack>

                        <Typography variant="caption" color="text.secondary">
                          {TelefonDate(order.created_at, lang)}
                        </Typography>
                      </Stack>
                    </Stack>
                  </Box>
                ))}
              </Stack>
            )}
          </CardContent>
        </Card>
      )}

      {tab === "calls" && (
        <Card sx={panelCardSx}>
          <CardContent sx={{ p: { xs: 2, md: 2.6 } }}>
            <Stack direction="row" spacing={1.2} alignItems="center" mb={2}>
              <CallReceivedIcon sx={{ color: BRAND.orange }} />
              <Box>
                <Typography fontWeight={950}>{TT.callHistory}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {incomingCalls.length}
                </Typography>
              </Box>
            </Stack>

            {incomingCalls.length === 0 ? (
              <Alert severity="info" sx={{ borderRadius: 3 }}>
                {TT.noCall}
              </Alert>
            ) : (
              <Grid container spacing={1.4}>
                {incomingCalls.map((call) => (
                  <Grid item xs={12} md={6} lg={4} key={call.id}>
                    <Box
                      sx={{
                        p: 1.6,
                        borderRadius: 4,
                        bgcolor: "#fff",
                        border: `1px solid ${alpha(BRAND.orange, 0.25)}`,
                        boxShadow: "0 10px 26px rgba(245,130,32,.10)",
                        animation: "telefonPulse 2.8s infinite",
                      }}
                    >
                      <Stack direction="row" spacing={1.2} alignItems="center">
                        <Box
                          sx={{
                            width: 44,
                            height: 44,
                            borderRadius: "50%",
                            display: "grid",
                            placeItems: "center",
                            bgcolor: BRAND.softOrange,
                            color: BRAND.orange,
                          }}
                        >
                          <PhoneInTalkIcon />
                        </Box>

                        <Box sx={{ minWidth: 0 }}>
                          <Typography fontWeight={950} noWrap>
                            {call.customer_name || call.phone || "-"}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {call.phone || "-"}
                          </Typography>
                        </Box>
                      </Stack>

                      {call.customer_address && (
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ mt: 1 }}
                        >
                          {call.customer_address}
                        </Typography>
                      )}

                      <Typography
                        variant="caption"
                        color="text.secondary"
                        display="block"
                        sx={{ mt: 1 }}
                      >
                        {TelefonDate(call.created_at, lang)}
                      </Typography>

                      <Stack direction="row" spacing={1} mt={1.4}>
                        <Button
                          fullWidth
                          variant="contained"
                          onClick={() => fillFromCall(call)}
                          sx={{
                            borderRadius: 999,
                            bgcolor: BRAND.blue,
                            fontWeight: 900,
                          }}
                        >
                          {TT.select}
                        </Button>

                        <Button
                          fullWidth
                          variant="outlined"
                          onClick={() => markCallRead(call)}
                          sx={{
                            borderRadius: 999,
                            fontWeight: 900,
                          }}
                        >
                          {TT.markRead}
                        </Button>
                      </Stack>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            )}
          </CardContent>
        </Card>
      )}

      <Card
        sx={{
          ...panelCardSx,
          mt: 2.2,
          bgcolor: "#fff",
        }}
      >
        <CardContent sx={{ p: { xs: 2, md: 2.4 } }}>
          <Stack direction="row" spacing={1.2} alignItems="center">
            <CheckCircleIcon sx={{ color: BRAND.success }} />
            <Box>
              <Typography fontWeight={950}>{TT.integrationReady}</Typography>
              <Typography variant="body2" color="text.secondary">
                {TT.integrationText}
              </Typography>
            </Box>
          </Stack>
        </CardContent>
      </Card>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3800}
        onClose={() =>
          setSnackbar((prev) => ({
            ...prev,
            open: false,
          }))
        }
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
      >
        <Alert
          severity={snackbar.severity}
          sx={{
            borderRadius: 3,
            fontWeight: 800,
            boxShadow: "0 18px 45px rgba(15,23,42,0.18)",
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

function StatCard({ icon, title, value, color }) {
  return (
    <Card
      sx={{
        borderRadius: 4,
        border: `1px solid ${BRAND.border}`,
        boxShadow: "0 14px 34px rgba(15,23,42,0.07)",
        animation: "telefonFadeUp .35s ease both",
        height: "100%",
      }}
    >
      <CardContent sx={{ p: { xs: 1.5, md: 2 } }}>
        <Stack direction="row" spacing={1.2} alignItems="center">
          <Box
            sx={{
              width: 42,
              height: 42,
              borderRadius: 3,
              display: "grid",
              placeItems: "center",
              bgcolor: alpha(color, 0.12),
              color,
              flexShrink: 0,
            }}
          >
            {icon}
          </Box>

          <Box sx={{ minWidth: 0 }}>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontWeight: 800 }}
              noWrap
            >
              {title}
            </Typography>

            <Typography
              sx={{
                fontWeight: 950,
                color: BRAND.dark,
                fontSize: { xs: "1.15rem", md: "1.45rem" },
                lineHeight: 1.15,
              }}
              noWrap
            >
              {value}
            </Typography>
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
}